import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Checkbox,
  Toolbar,
  Menu
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  QrCode2 as QrCode2Icon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { devicesAPI } from '../api/devices';
import { deviceTypesAPI } from '../api/deviceTypes';
import { locationsAPI } from '../api/locations';
import { characteristicsAPI } from '../api/characteristics';
import NetworkInterfaceManager from '../components/NetworkInterfaceManager';
import DeviceQrDialog from '../components/DeviceQrDialog';
import ImportDevicesDialog from '../components/ImportDevicesDialog';
import ImportScanDialog from '../components/ImportScanDialog';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function Devices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    device_type_id: '',
    location_id: null,
    description: '',
    characteristic_values: {}
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [bulkLocationDialog, setBulkLocationDialog] = useState(false);
  const [bulkLocationId, setBulkLocationId] = useState('');
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [qrDevice, setQrDevice] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Handle URL parameter for editing device
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && devices.length > 0) {
      const deviceToEdit = devices.find(d => d.id === parseInt(editId));
      if (deviceToEdit) {
        handleOpenDialog(deviceToEdit);
        // Remove the edit parameter from URL
        searchParams.delete('edit');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, devices]);

  const loadData = async () => {
    try {
      const [devs, types, locs, chars] = await Promise.all([
        devicesAPI.getAll(),
        deviceTypesAPI.getAll(),
        locationsAPI.getAll(),
        characteristicsAPI.getAll()
      ]);
      setDevices(devs.items || devs);
      setDeviceTypes(types);
      setLocations(locs);
      setCharacteristics(chars);
    } catch (err) {
      setError('Ошибка загрузки данных: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Get characteristics that should be shown in list
  const getListCharacteristics = () => {
    return characteristics.filter(char => char.show_in_list);
  };

  // Get all descendant location IDs (including the location itself)
  const getLocationDescendants = (locationId) => {
    const result = [locationId];

    const findChildren = (parentId) => {
      const children = locations.filter(loc => loc.parent_id === parentId);
      children.forEach(child => {
        result.push(child.id);
        findChildren(child.id);
      });
    };

    findChildren(locationId);
    return result;
  };

  const getSelectedDeviceType = () => {
    return deviceTypes.find(dt => dt.id === parseInt(formData.device_type_id));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);

      // Преобразуем characteristic_values из массива в объект {characteristic_id: value}
      const charValues = {};
      if (item.characteristic_values && Array.isArray(item.characteristic_values)) {
        item.characteristic_values.forEach(cv => {
          charValues[cv.characteristic_id] = cv.value;
        });
      }

      setFormData({
        name: item.name,
        device_type_id: item.device_type_id,
        location_id: item.location_id,
        description: item.description || '',
        characteristic_values: charValues
      });
      setTabValue(0);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        device_type_id: deviceTypes.length > 0 ? deviceTypes[0].id : '',
        location_id: null,
        description: '',
        characteristic_values: {}
      });
      setTabValue(0);
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'device_type_id') {
      // При смене типа устройства сбрасываем значения характеристик
      setFormData({
        ...formData,
        device_type_id: value,
        characteristic_values: {}
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleCharacteristicChange = (characteristicId, value) => {
    setFormData({
      ...formData,
      characteristic_values: {
        ...formData.characteristic_values,
        [characteristicId]: value
      }
    });
  };

  const validateRequiredCharacteristics = () => {
    const deviceType = getSelectedDeviceType();
    if (!deviceType || !deviceType.characteristics) return true;

    const requiredChars = deviceType.characteristics.filter(dtc => dtc.is_required);

    for (const dtc of requiredChars) {
      const value = formData.characteristic_values[dtc.characteristic_id];
      if (!value || value.trim() === '') {
        const char = characteristics.find(c => c.id === dtc.characteristic_id);
        setError(`Обязательное поле "${char?.name}" не заполнено`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    // Note: Required characteristics validation removed to allow incomplete devices
    // Users can save devices with only a name and fill required fields later

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        device_type_id: parseInt(formData.device_type_id),
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        description: formData.description || null,
        characteristic_values: formData.characteristic_values
      };

      if (editingItem) {
        await devicesAPI.update(editingItem.id, payload);
      } else {
        await devicesAPI.create(payload);
      }

      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Вы уверены, что хотите удалить устройство "${name}"?`)) {
      return;
    }

    try {
      await devicesAPI.delete(id);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleExportSummary = async () => {
    const params = new URLSearchParams();
    if (filterType) params.append('device_type_id', filterType);
    if (filterLocation) params.append('location_id', filterLocation);

    try {
      const response = await fetch(`/api/reports/devices/export/summary/excel?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'devices_summary.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Ошибка экспорта');
      }
    } catch (err) {
      setError('Ошибка экспорта: ' + err.message);
    }
    setExportAnchorEl(null);
  };

  const handleExportFull = async (format = 'excel') => {
    const params = new URLSearchParams();
    if (filterType) params.append('device_type_id', filterType);
    if (filterLocation) params.append('location_id', filterLocation);

    const formatExtensions = {
      'excel': { ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      'csv': { ext: 'csv', mime: 'text/csv' },
      'pdf': { ext: 'pdf', mime: 'application/pdf' }
    };

    const { ext } = formatExtensions[format];

    try {
      const response = await fetch(`/api/reports/devices/export/full/${format}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devices_full.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Ошибка экспорта');
      }
    } catch (err) {
      setError('Ошибка экспорта: ' + err.message);
    }
    setExportAnchorEl(null);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDevices(filteredDevices.map(d => d.id));
    } else {
      setSelectedDevices([]);
    }
  };

  const handleSelectOne = (deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Вы уверены, что хотите удалить ${selectedDevices.length} устройств?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/devices/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(selectedDevices)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка удаления');
      }

      const result = await response.json();
      setError('');
      alert(result.message);
      setSelectedDevices([]);
      loadData();
    } catch (err) {
      setError('Ошибка массового удаления: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateLocation = async () => {
    if (!bulkLocationId) {
      setError('Выберите локацию');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/devices/bulk-update-location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_ids: selectedDevices,
          location_id: parseInt(bulkLocationId)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка обновления');
      }

      const result = await response.json();
      setError('');
      alert(result.message);
      setSelectedDevices([]);
      setBulkLocationDialog(false);
      setBulkLocationId('');
      loadData();
    } catch (err) {
      setError('Ошибка массового обновления: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceTypeName = (typeId) => {
    const type = deviceTypes.find(t => t.id === typeId);
    return type ? type.name : '-';
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : '-';
  };

  // Get full location path from device (use location_full_path from backend)
  const getLocationFullPath = (device) => {
    return device.location_full_path || getLocationName(device.location_id);
  };

  const getCharacteristicValue = (device, characteristicId) => {
    if (!device.characteristic_values) return '-';
    const cv = device.characteristic_values.find(v => v.characteristic_id === characteristicId);
    return cv ? cv.value : '-';
  };

  const getCharacteristicName = (charId) => {
    const char = characteristics.find(c => c.id === charId);
    return char ? char.name : '-';
  };

  const renderCharacteristicInput = (dtc) => {
    const char = characteristics.find(c => c.id === dtc.characteristic_id);
    if (!char) return null;

    const value = formData.characteristic_values[dtc.characteristic_id] || char.default_value || '';
    const dataType = char.data_type;

    // Check if there's a saved value from the device
    const savedValue = editingItem?.characteristic_values?.find(cv => cv.characteristic_id === dtc.characteristic_id)?.value;
    const hasValue = savedValue && savedValue !== '';
    const helperText = hasValue
      ? `${char.description || ''}${char.description ? ' • ' : ''}✓ Заполнено`
      : `${char.description || ''}${char.description ? ' • ' : ''}Не заполнено`;

    let inputComponent;

    switch (dataType?.base_type) {
      case 'enum':
        inputComponent = (
          <TextField
            fullWidth
            select
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
          >
            {dataType.enum_values && dataType.enum_values.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
        break;

      case 'boolean':
        inputComponent = (
          <TextField
            fullWidth
            select
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
          >
            <MenuItem value="true">Да</MenuItem>
            <MenuItem value="false">Нет</MenuItem>
          </TextField>
        );
        break;

      case 'date':
        inputComponent = (
          <TextField
            fullWidth
            type="date"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
          />
        );
        break;

      case 'datetime':
        inputComponent = (
          <TextField
            fullWidth
            type="datetime-local"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
          />
        );
        break;

      case 'integer':
      case 'port':
        inputComponent = (
          <TextField
            fullWidth
            type="number"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      case 'float':
        inputComponent = (
          <TextField
            fullWidth
            type="number"
            inputProps={{ step: 0.01 }}
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      case 'text':
        inputComponent = (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      default: // string, ip, mac, cidr, json, etc.
        inputComponent = (
          <TextField
            fullWidth
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText || dataType?.help_text}
            placeholder={dataType?.placeholder}
          />
        );
    }

    return (
      <Grid item xs={12} md={6} key={dtc.characteristic_id}>
        {inputComponent}
      </Grid>
    );
  };

  const filteredDevices = devices.filter(device => {
    // Поиск по всем полям
    let matchesSearch = false;
    if (!searchTerm) {
      matchesSearch = true;
    } else {
      const search = searchTerm.toLowerCase();

      // Поиск по названию устройства
      if (device.name.toLowerCase().includes(search)) {
        matchesSearch = true;
      }

      // Поиск по типу устройства
      if (!matchesSearch && device.device_type_name?.toLowerCase().includes(search)) {
        matchesSearch = true;
      }

      // Поиск по локации (полный путь)
      if (!matchesSearch && device.location_full_path?.toLowerCase().includes(search)) {
        matchesSearch = true;
      } else if (!matchesSearch && device.location_name?.toLowerCase().includes(search)) {
        matchesSearch = true;
      }

      // Поиск по описанию
      if (!matchesSearch && device.description?.toLowerCase().includes(search)) {
        matchesSearch = true;
      }

      // Поиск по значениям характеристик
      if (!matchesSearch && device.characteristic_values) {
        for (const cv of device.characteristic_values) {
          if (cv.value?.toLowerCase().includes(search)) {
            matchesSearch = true;
            break;
          }
        }
      }
    }

    const matchesType = !filterType || device.device_type_id === parseInt(filterType);

    // Фильтр по локации (включая дочерние)
    let matchesLocation = true;
    if (filterLocation) {
      const locationIds = getLocationDescendants(parseInt(filterLocation));
      matchesLocation = locationIds.includes(device.location_id);
    }

    return matchesSearch && matchesType && matchesLocation;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Устройства</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            disabled={devices.length === 0}
          >
            Экспорт
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
          >
            <MenuItem onClick={handleExportSummary}>
              📊 Краткий отчет (Excel)
            </MenuItem>
            <MenuItem onClick={() => handleExportFull('excel')}>
              📋 Полный отчет (Excel)
            </MenuItem>
            <MenuItem onClick={() => handleExportFull('csv')}>
              📄 Полный отчет (CSV)
            </MenuItem>
            <MenuItem onClick={() => handleExportFull('pdf')}>
              📑 Полный отчет (PDF)
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportOpen(true)}
            disabled={deviceTypes.length === 0}
          >
            Импорт
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setScanOpen(true)}
            disabled={deviceTypes.length === 0}
          >
            Из скана
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={deviceTypes.length === 0}
          >
            Добавить устройство
          </Button>
        </Box>
      </Box>

      {deviceTypes.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Сначала создайте типы устройств!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Поиск по всем полям..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Тип устройства</InputLabel>
              <Select
                value={filterType}
                label="Тип устройства"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">Все типы</MenuItem>
                {deviceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Локация</InputLabel>
              <Select
                value={filterLocation}
                label="Локация"
                onChange={(e) => setFilterLocation(e.target.value)}
              >
                <MenuItem value="">Все локации</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {selectedDevices.length > 0 && (
        <Toolbar sx={{ bgcolor: 'primary.light', color: 'white', mb: 2, borderRadius: 1 }}>
          <Typography sx={{ flex: '1 1 100%' }} variant="subtitle1">
            Выбрано устройств: {selectedDevices.length}
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Удалить выбранные
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setBulkLocationDialog(true)}
            disabled={loading}
          >
            Изменить локацию
          </Button>
        </Toolbar>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedDevices.length > 0 && selectedDevices.length < filteredDevices.length}
                  checked={filteredDevices.length > 0 && selectedDevices.length === filteredDevices.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Локация</TableCell>
              <TableCell>Описание</TableCell>
              {getListCharacteristics().map(char => (
                <TableCell key={char.id}>{char.name}</TableCell>
              ))}
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.map((item) => (
              <TableRow key={item.id} selected={selectedDevices.includes(item.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDevices.includes(item.id)}
                    onChange={() => handleSelectOne(item.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/devices/${item.id}`)}
                  >
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>{getDeviceTypeName(item.device_type_id)}</TableCell>
                <TableCell>{getLocationFullPath(item)}</TableCell>
                <TableCell>{item.description || '-'}</TableCell>
                {getListCharacteristics().map(char => (
                  <TableCell key={char.id}>
                    {getCharacteristicValue(item, char.id)}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => setQrDevice(item)}
                    title="QR-метка"
                  >
                    <QrCode2Icon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(item)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item.id, item.name)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredDevices.length === 0 && (
              <TableRow>
                <TableCell colSpan={6 + getListCharacteristics().length} align="center">
                  {searchTerm || filterType || filterLocation ? 'Нет устройств по заданным фильтрам' : 'Нет устройств'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать устройство' : 'Добавить устройство'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Основное" />
            <Tab label="Характеристики" disabled={!formData.device_type_id} />
            <Tab label="Сетевые интерфейсы" disabled={!editingItem} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <TextField
              fullWidth
              label="Название"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              helperText="Уникальное название устройства"
            />

            <TextField
              fullWidth
              select
              label="Тип устройства"
              name="device_type_id"
              value={formData.device_type_id}
              onChange={handleChange}
              margin="normal"
              required
            >
              {deviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Локация"
              name="location_id"
              value={formData.location_id || ''}
              onChange={handleChange}
              margin="normal"
            >
              <MenuItem value="">Не указана</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Описание"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {formData.device_type_id && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Характеристики устройства
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Заполните характеристики для типа: {getDeviceTypeName(parseInt(formData.device_type_id))}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {getSelectedDeviceType()?.characteristics
                    ?.sort((a, b) => a.display_order - b.display_order)
                    .map(dtc => renderCharacteristicInput(dtc))}
                </Grid>

                {getSelectedDeviceType()?.characteristics?.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    У этого типа устройства нет привязанных характеристик. Добавьте их в разделе "Типы устройств".
                  </Alert>
                )}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {editingItem ? (
              <NetworkInterfaceManager deviceId={editingItem.id} />
            ) : (
              <Alert severity="info">
                Сначала сохраните устройство, чтобы добавить сетевые интерфейсы
              </Alert>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог массового изменения локации */}
      <Dialog open={bulkLocationDialog} onClose={() => setBulkLocationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Изменить локацию для {selectedDevices.length} устройств
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Новая локация</InputLabel>
            <Select
              value={bulkLocationId}
              label="Новая локация"
              onChange={(e) => setBulkLocationId(e.target.value)}
            >
              <MenuItem value="">Не выбрано</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkLocationDialog(false)}>Отмена</Button>
          <Button
            onClick={handleBulkUpdateLocation}
            variant="contained"
            disabled={loading || !bulkLocationId}
          >
            {loading ? 'Обновление...' : 'Применить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR label */}
      <DeviceQrDialog
        open={Boolean(qrDevice)}
        onClose={() => setQrDevice(null)}
        device={qrDevice ? { ...qrDevice, device_type_name: getDeviceTypeName(qrDevice.device_type_id) } : null}
      />

      {/* Bulk import */}
      <ImportDevicesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { setImportOpen(false); loadData(); }}
      />

      {/* Create one device from an agent/GLPI scan file */}
      <ImportScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onCreated={(deviceId) => { setScanOpen(false); navigate(`/devices/${deviceId}`); }}
      />
    </Box>
  );
}

export default Devices;
